// components/CollectionSubtitles/CollectionSubtitleSearch.jsx
import React, { useState, useEffect, useRef } from "react";
import { useVideo } from "../../context/VideoContext";
import { useSelectedSubtitles } from "../../context/SelectedSubtitlesContext";
import { findCollectionSubtitlesByInfo } from "../../services/subtitleApi";
import useVideoManager from "../../hooks/useVideoManager";
import { safeGetItem } from "../../utils/safeStorage";
import { getLanguageName } from "../../utils/languages";
import useVideoMetadata from "../../hooks/useVideoMetadata";
import CollectionConfirmModal from "./CollectionConfirmModal";
import "./CollectionSubtitleSearch.css";

const CollectionSubtitleSearch = ({ isOpen, onClose }) => {
  const { videoRef, videoSrc, setShowTutorialGuide } = useVideo();
  const { subtitleHistory } = useSelectedSubtitles();
  const videoMetadataManager = useVideoMetadata();

  // ⭐ گرفتن loadSubtitlesFromIds از useVideoManager
  const { loadSubtitlesFromIds } = useVideoManager();

  // State مدیریت
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // State فرم جستجو
  const [movieName, setMovieName] = useState("");
  const [videoType, setVideoType] = useState("movie");
  const [season, setSeason] = useState("");
  const [episode, setEpisode] = useState("");

  // State مجموعه فعلی
  const [currentCollectionIds, setCurrentCollectionIds] = useState({
    subOneID: null,
    subTwoID: null,
  });
  const [hideSearchModal, setHideSearchModal] = useState(false);

  // State برای نمایش جزئیات
  const [expandedItem, setExpandedItem] = useState(null);

  // State کنترل نمایش فرم

  // ⭐ State برای مودال تأیید
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);

  // Ref برای تشخیص اسکرول
  const modalContentRef = useRef(null);
  const resultsRef = useRef(null);

  // پر کردن پیش‌فرض فرم با اطلاعات فیلم فعلی
  useEffect(() => {
    if (isOpen) {
      const metadata = videoMetadataManager.videoMetadata;
      const extractedInfo = videoMetadataManager.extractInfoFromFileName(
        metadata?.fileName || "",
      );

      setMovieName(metadata?.filmName || extractedInfo?.filmName || "");
      setVideoType(metadata?.videoType || extractedInfo?.videoType || "movie");
      setSeason(metadata?.season || extractedInfo?.season || "");
      setEpisode(metadata?.episode || extractedInfo?.episode || "");

      // ریست نتایج قبلی
      setError(null);
      setHasSearched(false);
      setHideSearchModal(false); // ⭐ ریست
      setShowConfirmModal(false); // ⭐ ریست
    }
  }, [isOpen]);

  // پاک کردن هنگام بسته شدن
  useEffect(() => {
    if (!isOpen) {
      setSearchResults(null);
      setError(null);
      setHasSearched(false);
      setExpandedItem(null);
      setHideSearchModal(false);
      setShowConfirmModal(false);
    }
  }, [isOpen]);

  // تغییر نوع: برای فیلم فیلدهای فصل و قسمت مخفی می‌شوند
  const handleVideoTypeChange = (value) => {
    setVideoType(value);
  };

  // اسکرول خودکار به نتایج جستجو
  useEffect(() => {
    if (searchResults?.docs?.length && modalContentRef.current) {
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [searchResults]);

  // جستجوی مجموعه
  const handleSearch = async () => {
    if (!movieName.trim()) {
      setError("لطفاً نام فیلم یا سریال را وارد کنید");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSearchResults(null);
    setHasSearched(true);

    try {
      const response = await findCollectionSubtitlesByInfo(
        movieName.trim(),
        season,
        episode,
        videoType,
      );

      if (response.success) {
        setSearchResults(response.data);
        if (!response.data?.docs?.length) {
          setError("هیچ مجموعه‌ای پیدا نشد");
        }
      } else {
        setError(response.message || "خطا در جستجو");
      }
    } catch (err) {
      setError("خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.");
      console.error("Search error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // ⭐ تابع loadAndAddSubtitles با استفاده از loadSubtitlesFromIds
  const loadAndAddSubtitles = async (item) => {
    try {
      setIsLoading(true);
      setError(null);

      // ⭐ استفاده از همون تابعی که توی useVideoManager برای حالت exactly استفاده میشه
      await loadSubtitlesFromIds(
        item.subOneID,
        item.subTwoID,
        item.langOne,
        item.langTwo,
        item, // کل item به عنوان collectionInfo
      );

      // ذخیره مجموعه فعلی
      setCurrentCollectionIds({
        subOneID: item.subOneID,
        subTwoID: item.subTwoID,
      });

      // به‌روزرسانی metadata
      if (item.movieName) {
        videoMetadataManager.updateMetadataFromBackend(
          { success: true, data: { docs: [item] } },
          videoMetadataManager.videoMetadata?.fileName || "",
        );
      }
    } catch (err) {
      console.error("🔴 Load subtitles error:", err);
      setError("خطا در بارگذاری زیرنویس‌ها");
      throw err; // ⭐ پرتاب خطا برای handleAddClick
    } finally {
      setIsLoading(false);
    }
  };

  // ⭐ handleAddClick - منتظر بمون تا لود تموم بشه
  const handleAddClick = async (item, e) => {
    e.stopPropagation();
    setSelectedCollection(item);

    // ⭐ صبر کن تا زیرنویس کامل لود بشه
    await loadAndAddSubtitles(item);

    // ⭐ بعد مودال رو عوض کن
    setHideSearchModal(true);
    setShowConfirmModal(true);
  };

  // ⭐ تأیید - بستن مودال جستجو و ادامه تماشا
  const handleConfirm = () => {
    setShowConfirmModal(false);
    setHideSearchModal(false);
    setSelectedCollection(null);
    const tutorialDismissed = safeGetItem("tutorialGuideDismissedAt");
    if (!tutorialDismissed) {
      setShowTutorialGuide(true);
    }
    onClose();
  };

  // ⭐ برگشت - موندن توی مودال جستجو
  const handleBack = () => {
    setShowConfirmModal(false);
    setHideSearchModal(false);
  };

  // رندر کارت هر مجموعه
  const renderCollectionCard = (item, index) => {
    const isCurrentCollection =
      currentCollectionIds.subOneID === item.subOneID &&
      currentCollectionIds.subTwoID === item.subTwoID;

    const isInHistory = subtitleHistory.some(
      (h) => h.collectionId === item.collectionId,
    );

    let cardClass = "collection-card";
    if (isCurrentCollection) cardClass += " current-collection";
    if (isInHistory && !isCurrentCollection) cardClass += " in-history";

    return (
      <div
        key={item._id || index}
        className={cardClass}
        onClick={() => setExpandedItem(expandedItem === index ? null : index)}
      >
        <div className="card-header">
          {item.coverUrl && (
            <img
              src={item.coverUrl}
              alt={item.movieName}
              className="card-cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}
          <div className="card-info">
            <h4 className="card-title">
              {item.movieName}
              {isCurrentCollection && (
                <span className="current-badge">✓ فعال</span>
              )}
              {isInHistory && !isCurrentCollection && (
                <span className="history-badge">تاریخچه</span>
              )}
            </h4>

            <div className="card-metadata">
              <span className="video-type">
                {item.videoType === "series" || item.videoType === "serial"
                  ? "📺 سریال"
                  : "🎬 فیلم"}
              </span>

              {(item.videoType === "series" || item.videoType === "serial") &&
                item.season && (
                <span className="season-episode">
                  فصل {item.season}
                  {item.episode ? ` - قسمت ${item.episode}` : ""}
                </span>
              )}
            </div>

            <div className="card-languages">
              <span className="lang-one" title="زیرنویس اول">
                {getLanguageName(item.langOne)}
              </span>
              <span className="lang-two" title="زیرنویس دوم">
                {getLanguageName(item.langTwo)}
              </span>
            </div>
          </div>
        </div>

        <button
          className="add-subtitles-btn"
          onClick={(e) => handleAddClick(item, e)}
          disabled={isLoading}
        >
          {isLoading ? "⏳ در حال بارگذاری..." : "➕ افزودن به فیلم"}
        </button>

        {expandedItem === index && (
          <div className="card-details">
            <div className="detail-row">
              <span>شناسه مجموعه:</span>
              <span className="id-text">{item.collectionId}</span>
            </div>
            <div className="detail-row">
              <span>زیرنویس ۱:</span>
              <span className="id-text">{item.subOneID}</span>
            </div>
            <div className="detail-row">
              <span>زیرنویس ۲:</span>
              <span className="id-text">{item.subTwoID}</span>
            </div>
            {item.imdbId && (
              <div className="detail-row">
                <span>IMDB:</span>
                <a
                  href={`https://www.imdb.com/title/${item.imdbId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {item.imdbId}
                </a>
              </div>
            )}
            {item.movieFileName && (
              <div className="detail-row">
                <span>نام فایل:</span>
                <span className="file-name">{item.movieFileName}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="collection-search-overlay"
        style={{ display: hideSearchModal ? "none" : "flex" }}
      >
        <div className="collection-search-modal">
          {/* هدر */}
          <div className="modal-header">
            <h3>🔍 جستجوی زیرنویس دوگانه</h3>
            <button className="close-btn" onClick={onClose}>
              ✕
            </button>
          </div>

          {/* محتوای اسکرول‌شونده */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="modal-scroll-content"
            ref={modalContentRef}
          >
            {/* فرم جستجو - با اسکرول محو میشه */}
            <div className="search-form-container">

              {/* فرم جستجو */}
              <div className="search-form">
                <div className="search-inputs">
                  <div className="input-group">
                    <label>نوع</label>
                    <select
                      value={videoType}
                      onChange={(e) => handleVideoTypeChange(e.target.value)}
                      className="search-input small-input"
                    >
                      <option value="movie">فیلم</option>
                      <option value="series">سریال</option>
                    </select>
                  </div>

                  <div className="input-group main-input">
                    <label>نام فیلم/سریال *</label>
                    <input
                      type="text"
                      value={movieName}
                      onChange={(e) => setMovieName(e.target.value)}
                      placeholder="مثال: Friends"
                      className="search-input"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                    />
                  </div>

                  {videoType === "series" && (
                    <>
                      <div className="input-group">
                        <label>فصل</label>
                        <input
                          type="number"
                          value={season}
                          onChange={(e) => setSeason(e.target.value)}
                          placeholder="فصل"
                          className="search-input small-input"
                          min="1"
                        />
                      </div>

                      <div className="input-group">
                        <label>قسمت</label>
                        <input
                          type="number"
                          value={episode}
                          onChange={(e) => setEpisode(e.target.value)}
                          placeholder="قسمت"
                          className="search-input small-input"
                          min="1"
                        />
                      </div>
                    </>
                  )}
                </div>

                <button
                  className="search-btn"
                  onClick={handleSearch}
                  disabled={isLoading}
                >
                  {isLoading ? "⏳ در حال جستجو..." : "🔎 جستجوی مجموعه"}
                </button>
              </div>
            </div>

            {/* پیام خطا */}
            {error && !isLoading && (
              <div className="error-message">
                <span>⚠️</span> {error}
              </div>
            )}

            {/* نتایج جستجو */}
            {searchResults &&
              searchResults.docs &&
              searchResults.docs.length > 0 && (
                <div className="results-section" ref={resultsRef}>
                  <div className="results-header">
                    <h4>
                      نتایج جستجو
                      <span className="results-count">
                        ({searchResults.totalDocs} مجموعه یافت شد)
                      </span>
                    </h4>
                  </div>

                  <div className="results-grid">
                    {searchResults.docs.map((doc, index) =>
                      renderCollectionCard(doc, index),
                    )}
                  </div>

                  {/* صفحه‌بندی */}
                  {searchResults.totalPages > 1 && (
                    <div className="pagination-info">
                      صفحه {searchResults.page} از {searchResults.totalPages} |
                      مجموع: {searchResults.totalDocs} مجموعه
                    </div>
                  )}
                </div>
              )}

            {/* عدم نتیجه */}
            {searchResults &&
              (!searchResults.docs || searchResults.docs.length === 0) &&
              !isLoading && (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <p>نتیجه‌ای یافت نشد</p>
                  <p className="suggestion">
                    نام فیلم را دقیق‌تر وارد کنید یا فیلدهای فصل و قسمت را تغییر
                    دهید
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* ⭐ مودال تأیید همخوانی */}
      <CollectionConfirmModal
        isOpen={showConfirmModal}
        onBack={handleBack}
        onConfirm={handleConfirm}
        collectionInfo={selectedCollection}
      />
    </>
  );
};

export default CollectionSubtitleSearch;
